import React, {
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import {
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography
} from "@mui/material";

import {
    DataGrid
} from "@mui/x-data-grid";
 import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
    getRailways,
    currentMonth,
    getIOHList,
    createIOH,
    updateIOH,
    deleteIOH
} from "../../../services/iohService";
import PageHeader from "../../../utils/PageHeader";


export default function IOHDataEntry() {

    // ============================================================
    // STATE
    // ============================================================

    const [railways, setRailways] = useState([]);
    const [rows, setRows] = useState([]);

    const [month, setMonth] = useState("");
    const [year, setYear] = useState("");

    const [form, setForm] = useState({
        rail: "",
        trolly_type: "ICF",
        nooftrolly: 0,
        labourcost: 0,
        materialcost: 0
    });

    const [editMode, setEditMode] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    const [deleteDialog, setDeleteDialog] = useState(false);
    const [loading, setLoading] = useState(false);


    // ============================================================
    // REFS - KEYBOARD DATA ENTRY
    // ============================================================

    const railwayRef = useRef(null);
    const trolleyTypeRef = useRef(null);
    const noTrolleyRef = useRef(null);
    const labourRef = useRef(null);
    const materialRef = useRef(null);
    const saveRef = useRef(null);


    // ============================================================
    // FORM TOTAL
    // ============================================================

    const formTotal = useMemo(() => {

        return (
            Number(form.labourcost || 0) +
            Number(form.materialcost || 0)
        );

    }, [
        form.labourcost,
        form.materialcost
    ]);


    // ============================================================
    // COACH SET
    // ============================================================

    const formCoachSet = useMemo(() => {

        return Number(form.nooftrolly || 0) / 2;

    }, [
        form.nooftrolly
    ]);


    // ============================================================
    // INITIAL LOAD
    // ============================================================

    useEffect(() => {

        loadInitialData();

    }, []);


    // ============================================================
    // INITIAL DATA
    // ============================================================

    const loadInitialData = async () => {

        try {

            setLoading(true);

            const [
                railwayResponse,
                monthResponse
            ] = await Promise.all([
                getRailways(),
                currentMonth()
            ]);

            setRailways(
                railwayResponse.data.data || []
            );

            const currentMonthData =
                monthResponse.data.data;

            const currentMonthValue =
                currentMonthData.month;

            const currentYearValue =
                currentMonthData.year;

            setMonth(currentMonthValue);
            setYear(currentYearValue);

            await loadList(
                currentMonthValue,
                currentYearValue
            );

            setTimeout(() => {

                railwayRef.current?.focus();

            }, 300);

        } catch (error) {

            console.error(
                "Initial load error:",
                error
            );

            alert(
                error.response?.data?.message ||
                "Unable to load IOH data"
            );

        } finally {

            setLoading(false);

        }

    };


    // ============================================================
    // LOAD LIST
    // ============================================================

    const loadList = async (
        selectedMonth = month,
        selectedYear = year
    ) => {

        if (
            !selectedMonth ||
            !selectedYear
        ) {
            return;
        }

        try {

            setLoading(true);

            const response =
                await getIOHList(
                    selectedMonth,
                    selectedYear
                );

            const data =
                response.data.data || [];

            const formattedRows =
                data.map(row => ({

                    ...row,

                    id: row.srno,

                    nooftrolly:
                        Number(
                            row.nooftrolly || 0
                        ),

                    coachset:
                        Number(
                            row.nooftrolly || 0
                        ) / 2,

                    labourcost:
                        Number(
                            row.labourcost || 0
                        ),

                    materialcost:
                        Number(
                            row.materialcost || 0
                        ),

                    total:
                        Number(
                            row.labourcost || 0
                        ) +
                        Number(
                            row.materialcost || 0
                        )

                }));

            setRows(formattedRows);

        } catch (error) {

            console.error(
                "Load list error:",
                error
            );

            alert(
                error.response?.data?.message ||
                "Unable to load IOH list"
            );

        } finally {

            setLoading(false);

        }

    };


    // ============================================================
    // HANDLE CHANGE
    // ============================================================

    const handleChange = (event) => {

        const {
            name,
            value
        } = event.target;

        setForm(prev => ({

            ...prev,

            [name]:

                [
                    "nooftrolly",
                    "labourcost",
                    "materialcost"
                ].includes(name)

                    ? Number(value)

                    : value

        }));

    };


    // ============================================================
    // KEYBOARD NAVIGATION
    // ============================================================

    const handleEnter = (
        event,
        nextRef,
        save = false
    ) => {

        if (event.key !== "Enter") {
            return;
        }

        event.preventDefault();

        if (save) {

            handleSave();

            return;

        }

        nextRef?.current?.focus();

    };


    // ============================================================
    // ESCAPE KEY
    // ============================================================

    const handleEscape = (event) => {

        if (
            event.key === "Escape" &&
            editMode
        ) {

            event.preventDefault();

            resetForm();

            setTimeout(() => {

                railwayRef.current?.focus();

            }, 50);

        }

    };


    // ============================================================
    // GLOBAL FORM KEYBOARD
    // ============================================================

    useEffect(() => {

        const handleKeyDown = (event) => {

            handleEscape(event);

        };

        window.addEventListener(
            "keydown",
            handleKeyDown
        );

        return () => {

            window.removeEventListener(
                "keydown",
                handleKeyDown
            );

        };

    }, [editMode]);


    // ============================================================
    // RESET FORM
    // ============================================================

    const resetForm = () => {

        setForm({

            rail: "",

            trolly_type: "ICF",

            nooftrolly: 0,

            labourcost: 0,

            materialcost: 0

        });

        setEditMode(false);

        setSelectedRow(null);

    };


    // ============================================================
    // SAVE / UPDATE
    // ============================================================

    const handleSave = async () => {

        if (!form.rail) {

            alert(
                "Please select Railway"
            );

            railwayRef.current?.focus();

            return;

        }


        if (!form.trolly_type) {

            alert(
                "Please select Trolley Type"
            );

            trolleyTypeRef.current?.focus();

            return;

        }


        if (
            Number(form.nooftrolly) <= 0
        ) {

            alert(
                "Please enter No of Trolley"
            );

            noTrolleyRef.current?.focus();

            return;

        }


        if (
            Number(form.labourcost || 0) <= 0 &&
            Number(form.materialcost || 0) <= 0
        ) {

            alert(
                "Please enter Labour Cost or Material Cost"
            );

            labourRef.current?.focus();

            return;

        }


        const payload = {

            rail:
                form.rail,

            trolly_type:
                form.trolly_type,

            nooftrolly:
                Number(
                    form.nooftrolly
                ),

            labourcost:
                Number(
                    form.labourcost
                ) || 0,

            materialcost:
                Number(
                    form.materialcost
                ) || 0,

            month:
                month,

            year:
                year,

            user:
                localStorage.getItem(
                    "username"
                ) || ""

        };


        try {

            setLoading(true);

            if (editMode) {

                await updateIOH(

                    selectedRow.srno,

                    payload

                );

            } else {




                await createIOH(
                    payload
                );

            }


            resetForm();

            await loadList(
                month,
                year
            );


            setTimeout(() => {

                railwayRef.current?.focus();

            }, 100);


        } catch (error) {

            console.error(
                "Save error:",
                error
            );

            alert(
                error.response?.data?.message ||
                "Unable to save IOH entry"
            );

        } finally {

            setLoading(false);

        }

    };


    // ============================================================
    // EDIT
    // ============================================================

    const handleEdit = (row) => {

        setForm({

            rail:
                row.rail,

            trolly_type:
                row.trolly_type,

            nooftrolly:
                Number(
                    row.nooftrolly || 0
                ),

            labourcost:
                Number(
                    row.labourcost || 0
                ),

            materialcost:
                Number(
                    row.materialcost || 0
                )

        });

        setSelectedRow(row);

        setEditMode(true);

        setTimeout(() => {

            railwayRef.current?.focus();

        }, 100);

    };


    // ============================================================
    // DELETE
    // ============================================================

    const openDeleteDialog = (row) => {

        setSelectedRow(row);

        setDeleteDialog(true);

    };


    const handleDelete = async () => {







        if (!selectedRow) {
            return;
        }

        try {

            setLoading(true);

            await deleteIOH(

                selectedRow.srno,
                month,
           year,

            );




            setDeleteDialog(false);

            setSelectedRow(null);

            await loadList(
                month,
                year
            );

        } catch (error) {

            console.error(
                "Delete error:",
                error
            );

            alert(
                error.response?.data?.message ||
                "Unable to delete IOH entry"
            );

        } finally {

            setLoading(false);

        }

    };


    // ============================================================
    // GRAND TOTAL
    // ============================================================

    const grandTotal = useMemo(() => {

        return rows.reduce(

            (acc, row) => {

                acc.nooftrolly +=
                    Number(
                        row.nooftrolly || 0
                    );

                acc.coachset +=
                    Number(
                        row.nooftrolly || 0
                    ) / 2;

                acc.labourcost +=
                    Number(
                        row.labourcost || 0
                    );

                acc.materialcost +=
                    Number(
                        row.materialcost || 0
                    );

                acc.total +=

                    Number(
                        row.labourcost || 0
                    ) +

                    Number(
                        row.materialcost || 0
                    );

                return acc;

            },

            {
                nooftrolly: 0,
                coachset: 0,
                labourcost: 0,
                materialcost: 0,
                total: 0
            }

        );

    }, [rows]);


    // ============================================================
    // RAILWAY TOTALS
    // ============================================================

    const railwayTotals = useMemo(() => {

        const totals = {};

        rows.forEach(row => {

            if (!totals[row.rail]) {

                totals[row.rail] = {

                    rail:
                        row.rail,

                    nooftrolly: 0,

                    coachset: 0,

                    labourcost: 0,

                    materialcost: 0,

                    total: 0

                };

            }


            totals[row.rail].nooftrolly +=

                Number(
                    row.nooftrolly || 0
                );


            totals[row.rail].coachset +=

                Number(
                    row.nooftrolly || 0
                ) / 2;


            totals[row.rail].labourcost +=

                Number(
                    row.labourcost || 0
                );


            totals[row.rail].materialcost +=

                Number(
                    row.materialcost || 0
                );


            totals[row.rail].total +=

                Number(
                    row.labourcost || 0
                ) +

                Number(
                    row.materialcost || 0
                );

        });

        return Object.values(totals);

    }, [rows]);


    // ============================================================
    // GRID COLUMNS
    // ============================================================

    const columns = [

        {
            field: "srno",
            headerName: "Sr No",
            width: 75
        },

        {
            field: "rail",
            headerName: "Railway",
            width: 100
        },

        {
            field: "trolly_type",
            headerName: "Trolley Type",
            width: 120
        },

        {
            field: "nooftrolly",
            headerName: "No of Trolley",
            width: 130
        },

        {
            field: "coachset",
            headerName: "Coach Set",
            width: 110
        },

        {
            field: "labourcost",
            headerName: "Labour Cost",
            width: 120
        },

        {
            field: "materialcost",
            headerName: "Material Cost",
            width: 130
        },

        {
            field: "total",
            headerName: "Total",
            width: 120
        },

        {

            field: "actions",

            headerName: "Action",

            width: 180,

            sortable: false,

            filterable: false,

            renderCell: (params) => (

                <Box
                    sx={{
                        display: "flex",
                        gap: 1,
                        width: "100%",
                        justifyContent: "center"
                    }}
                >

                    <Button
                        size="small"
                        variant="contained"
                        onClick={() =>
                            handleEdit(
                                params.row
                            )
                        }
                    >
                        Edit
                    </Button>


                    <Button
                        size="small"
                        variant="contained"
                        color="error"
                        onClick={() =>
                            openDeleteDialog(
                                params.row
                            )
                        }
                    >
                        Delete
                    </Button>

                </Box>

            )

        }

    ];


    // ============================================================
    // MONTH NAME
    // ============================================================

    const monthName = month

        ? new Date(
            year,
            month - 1,
            1
        ).toLocaleString(
            "en-US",
            {
                month: "long"
            }
        )

        : "";


    // ============================================================
    // COMMON INPUT STYLE
    // ============================================================

    const inputSx = {

        width: "100%",

        "& .MuiInputBase-root": {

            backgroundColor: "#fff",

            fontWeight: 500,

            minHeight: 56

        },

        "& .MuiInputLabel-root": {

            fontWeight: 500

        }

    };


    // ============================================================
    // READ ONLY FIELD STYLE
    // ============================================================

    const readOnlySx = {

        ...inputSx,

        "& .MuiInputBase-root": {

            backgroundColor: "#f0f0f0",

            fontWeight: 600,

            minHeight: 56

        }

    };


    // ============================================================
    // UI
    // ============================================================

    return (

        <Box
            sx={{

                width: "100%",

                maxWidth: "100%",

                minHeight: "100vh",

                boxSizing: "border-box",

                overflowX: "hidden",

                backgroundColor: "#f5f7fa",

                p: {

                    xs: 1,

                    sm: 2,

                    md: 3

                }

            }}
        >


            {/* =====================================================
                PAGE HEADER
            ===================================================== */}

           <PageHeader
        title=" IOH Data Entry"
        subtitle={`Data entry status for the month of: ${monthName} - ${year}`}
        icon={AccessTimeIcon}
       // statusLabel={'Records: ' + totalRows}
       //  statusValue="CSV File"
        statusChip={{
          label: loading
            ? "Loading..."
            : grandTotal.total > 0
              ? "Grand Total : " + grandTotal.total.toFixed(2)
              : "No Data",
          color: loading ? "warning" : grandTotal.total > 0 ? "primary" : "default",
        }}
       // showRefresh
       // onRefresh={handleRefresh}
        // refreshing={loading}
      />


            {/* =====================================================
                DATA ENTRY FORM
            ===================================================== */}

            <Card
                elevation={3}
                sx={{

                    width: "100%",

                    borderRadius: 2,

                    boxSizing: "border-box",

                    borderTop: "4px solid",

                    borderColor:

                        editMode

                            ? "warning.main"

                            : "success.main"

                }}
            >

                <CardContent
                    sx={{

                        p: {

                            xs: 1.5,

                            sm: 2,

                            md: 2.5

                        }

                    }}
                >


                    {/* FORM TITLE */}

                    <Box
                        sx={{

                            display: "flex",

                            justifyContent:
                                "space-between",

                            alignItems:
                                "center",

                            mb: 2,

                            flexWrap: "wrap",

                            gap: 1

                        }}
                    >

                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 700
                            }}
                        >

                            {editMode

                                ? "Edit IOH Entry"

                                : "New IOH Entry"

                            }

                        </Typography>


                        <Typography
                            variant="caption"
                            sx={{

                                color:
                                    "text.secondary",

                                display: {

                                    xs: "none",

                                    sm: "block"

                                }

                            }}
                        >
                            Press Enter to move to next field
                        </Typography>

                    </Box>


                    {/* =================================================
                        RESPONSIVE ENTRY GRID
                    ================================================= */}

                    <Box
                        className="ioh-entry-grid"
                        sx={{

                            display: "grid",

                            gridTemplateColumns: {

                                xs:
                                    "1fr",

                                sm:
                                    "repeat(2, minmax(0, 1fr))",

                                md:
                                    "repeat(3, minmax(0, 1fr))",

                                lg:
                                    "repeat(4, minmax(0, 1fr))",

                                xl:
                                    "repeat(8, minmax(0, 1fr))"

                            },

                            gap: {

                                xs: 1.25,

                                sm: 1.5,

                                md: 1.75

                            },

                            alignItems: "stretch",

                            width: "100%"

                        }}
                    >


                        {/* =================================================
                            RAILWAY
                        ================================================= */}

                        <Box sx={{ minWidth: 0 }}>

                            <FormControl
                                fullWidth
                                sx={inputSx}
                            >

                                <InputLabel>
                                    Railway
                                </InputLabel>

                                <Select
                                    inputRef={
                                        railwayRef
                                    }

                                    name="rail"

                                    value={
                                        form.rail
                                    }

                                    label="Railway"

                                    onChange={
                                        handleChange
                                    }

                                    onKeyDown={
                                        (event) =>
                                            handleEnter(
                                                event,
                                                trolleyTypeRef
                                            )
                                    }
                                >

                                    {railways.map(
                                        railway => (

                                            <MenuItem
                                                key={
                                                    railway.railway_code
                                                }

                                                value={
                                                    railway.railway_code
                                                }
                                            >

                                                {
                                                    railway.railway_code
                                                }

                                                {" - "}

                                                {
                                                    railway.railway_name
                                                }

                                            </MenuItem>

                                        )
                                    )}

                                </Select>

                            </FormControl>

                        </Box>


                        {/* =================================================
                            TROLLEY TYPE
                        ================================================= */}

                        <Box sx={{ minWidth: 0 }}>

                            <FormControl
                                fullWidth
                                sx={inputSx}
                            >

                                <InputLabel>
                                    Trolley Type
                                </InputLabel>

                                <Select
                                    inputRef={
                                        trolleyTypeRef
                                    }

                                    name="trolly_type"

                                    value={
                                        form.trolly_type
                                    }

                                    label="Trolley Type"

                                    onChange={
                                        handleChange
                                    }

                                    onKeyDown={
                                        (event) =>
                                            handleEnter(
                                                event,
                                                noTrolleyRef
                                            )
                                    }
                                >

                                    <MenuItem value="ICF">
                                        ICF
                                    </MenuItem>

                                    <MenuItem value="LHB">
                                        LHB
                                    </MenuItem>

                                </Select>

                            </FormControl>

                        </Box>


                        {/* =================================================
                            NO OF TROLLEY
                        ================================================= */}

                        <Box sx={{ minWidth: 0 }}>

                            <TextField
                                fullWidth

                                inputRef={
                                    noTrolleyRef
                                }

                                type="number"

                                label="No of Trolley"

                                name="nooftrolly"

                                value={
                                    form.nooftrolly
                                }

                                onChange={
                                    handleChange
                                }

                                onKeyDown={
                                    (event) =>
                                        handleEnter(
                                            event,
                                            labourRef
                                        )
                                }

                                inputProps={{

                                    min: 0,

                                    step: 1

                                }}

                                sx={inputSx}

                            />

                        </Box>


                        {/* =================================================
                            COACH SET
                        ================================================= */}

                        <Box sx={{ minWidth: 0 }}>

                            <TextField
                                fullWidth

                                label="Coach Set"

                                value={
                                    formCoachSet
                                }

                                InputProps={{
                                    readOnly: true
                                }}

                                sx={readOnlySx}

                            />

                        </Box>


                        {/* =================================================
                            LABOUR COST
                        ================================================= */}

                        <Box sx={{ minWidth: 0 }}>

                            <TextField
                                fullWidth

                                inputRef={
                                    labourRef
                                }

                                type="number"

                                label="Labour Cost"

                                name="labourcost"

                                value={
                                    form.labourcost
                                }

                                onChange={
                                    handleChange
                                }

                                onKeyDown={
                                    (event) =>
                                        handleEnter(
                                            event,
                                            materialRef
                                        )
                                }

                                inputProps={{

                                    min: 0,

                                    step: 0.01

                                }}

                                sx={inputSx}

                            />

                        </Box>


                        {/* =================================================
                            MATERIAL COST
                        ================================================= */}

                        <Box sx={{ minWidth: 0 }}>

                            <TextField
                                fullWidth

                                inputRef={
                                    materialRef
                                }

                                type="number"

                                label="Material Cost"

                                name="materialcost"

                                value={
                                    form.materialcost
                                }

                                onChange={
                                    handleChange
                                }

                                onKeyDown={
                                    (event) =>
                                        handleEnter(
                                            event,
                                            saveRef,
                                            true
                                        )
                                }

                                inputProps={{

                                    min: 0,

                                    step: 0.01

                                }}

                                sx={inputSx}

                            />

                        </Box>


                        {/* =================================================
                            TOTAL
                        ================================================= */}

                        <Box sx={{ minWidth: 0 }}>

                            <TextField
                                fullWidth

                                label="Total"

                                value={
                                    formTotal
                                }

                                InputProps={{
                                    readOnly: true
                                }}

                                sx={readOnlySx}

                            />

                        </Box>


                        {/* =================================================
                            SAVE
                        ================================================= */}

                        <Box sx={{ minWidth: 0 }}>

                            <Button
                                fullWidth

                                ref={saveRef}

                                variant="contained"

                                color={

                                    editMode

                                        ? "warning"

                                        : "success"

                                }

                                onClick={
                                    handleSave
                                }

                                disabled={
                                    loading
                                }

                                sx={{

                                    height: 56,

                                    minHeight: 56,

                                    fontWeight: 700,

                                    fontSize: {

                                        xs:
                                            "0.9rem",

                                        sm:
                                            "1rem"

                                    }

                                }}
                            >

                                {editMode

                                    ? "Update"

                                    : "Save"

                                }

                            </Button>

                        </Box>


                        {/* =================================================
                            CANCEL
                        ================================================= */}

                        {editMode && (

                            <Box
                                sx={{

                                    gridColumn:
                                        "1 / -1",

                                    display:
                                        "flex",

                                    justifyContent: {

                                        xs:
                                            "stretch",

                                        sm:
                                            "flex-start"

                                    }

                                }}
                            >

                                <Button
                                    fullWidth={
                                        true
                                    }

                                    sx={{

                                        width: {

                                            xs:
                                                "100%",

                                            sm:
                                                "auto"

                                        }

                                    }}

                                    variant="outlined"

                                    color="warning"

                                    onClick={() => {

                                        resetForm();

                                        setTimeout(() => {

                                            railwayRef
                                                .current
                                                ?.focus();

                                        }, 50);

                                    }}
                                >

                                    Cancel Edit

                                </Button>

                            </Box>

                        )}

                    </Box>

                </CardContent>

            </Card>


            {/* =====================================================
                RAILWAY TOTAL
            ===================================================== */}

            <Card
                elevation={2}
                sx={{

                    width: "100%",

                    mt: 2,

                    borderRadius: 2,

                    boxSizing: "border-box"

                }}
            >

                <CardContent
                    sx={{

                        p: {

                            xs: 1.5,

                            sm: 2

                        }

                    }}
                >

                    <Typography
                        variant="h6"
                        sx={{

                            mb: 1.5,

                            fontWeight: 700,

                            fontSize: {

                                xs:
                                    "1.05rem",

                                sm:
                                    "1.25rem"

                            }

                        }}
                    >
                        Railway-wise Total
                    </Typography>


                    <Box
                        sx={{

                            width: "100%",

                            overflowX: "auto",

                            WebkitOverflowScrolling:
                                "touch"

                        }}
                    >

                        <table
                            style={{

                                width: "100%",

                                minWidth: "650px",

                                borderCollapse:
                                    "collapse"

                            }}
                        >

                            <thead>

                                <tr>

                                    <th
                                        style={
                                            tableHeaderStyle
                                        }
                                    >
                                        Railway
                                    </th>

                                    <th
                                        style={
                                            tableHeaderStyle
                                        }
                                    >
                                        No of Trolley
                                    </th>

                                    <th
                                        style={
                                            tableHeaderStyle
                                        }
                                    >
                                        Coach Set
                                    </th>

                                    <th
                                        style={
                                            tableHeaderStyle
                                        }
                                    >
                                        Labour Cost
                                    </th>

                                    <th
                                        style={
                                            tableHeaderStyle
                                        }
                                    >
                                        Material Cost
                                    </th>

                                    <th
                                        style={
                                            tableHeaderStyle
                                        }
                                    >
                                        Total
                                    </th>

                                </tr>

                            </thead>


                            <tbody>

                                {railwayTotals.map(
                                    item => (

                                        <tr
                                            key={
                                                item.rail
                                            }
                                        >

                                            <td
                                                style={
                                                    tableCellStyle
                                                }
                                            >
                                                {
                                                    item.rail
                                                }
                                            </td>

                                            <td
                                                style={
                                                    tableCellStyle
                                                }
                                            >
                                                {
                                                    item.nooftrolly
                                                }
                                            </td>

                                            <td
                                                style={
                                                    tableCellStyle
                                                }
                                            >
                                                {
                                                    item.coachset
                                                }
                                            </td>

                                            <td
                                                style={
                                                    tableCellStyle
                                                }
                                            >
                                                {
                                                    item.labourcost
                                                }
                                            </td>

                                            <td
                                                style={
                                                    tableCellStyle
                                                }
                                            >
                                                {
                                                    item.materialcost
                                                }
                                            </td>

                                            <td
                                                style={
                                                    tableCellStyle
                                                }
                                            >
                                                {
                                                    item.total
                                                }
                                            </td>

                                        </tr>

                                    )
                                )}

                            </tbody>

                        </table>

                    </Box>

                </CardContent>

            </Card>


            {/* =====================================================
                DATA GRID
            ===================================================== */}

            <Card
                elevation={2}
                sx={{

                    width: "100%",

                    mt: 2,

                    borderRadius: 2,

                    overflow: "hidden"

                }}
            >

                <Box
                    sx={{

                        width: "100%",

                        height: {

                            xs: 430,

                            sm: 480,

                            md: 540,

                            lg: 600

                        },

                        minHeight: 360,

                        overflow: "hidden"

                    }}
                >

                    <DataGrid

                        rows={rows}

                        columns={columns}

                        loading={loading}

                        disableRowSelectionOnClick

                        pageSizeOptions={[
                            10,
                            25,
                            50
                        ]}

                        initialState={{

                            pagination: {

                                paginationModel: {

                                    page: 0,

                                    pageSize: 10

                                }

                            }

                        }}

                        sx={{

                            border: 0,

                            width: "100%",

                            "& .MuiDataGrid-columnHeaders": {

                                fontWeight: 700

                            },

                            "& .MuiDataGrid-columnHeaderTitle": {

                                fontWeight: 700

                            },

                            "& .MuiDataGrid-row:hover": {

                                backgroundColor:
                                    "rgba(25,118,210,0.05)"

                            },

                            "& .MuiDataGrid-cell": {

                                outline: "none"

                            }

                        }}

                    />

                </Box>

            </Card>


            {/* =====================================================
                GRAND TOTAL
            ===================================================== */}

            <Card
                elevation={3}
                sx={{

                    width: "100%",

                    mt: 2,

                    borderRadius: 2

                }}
            >

                <CardContent
                    sx={{

                        p: {

                            xs: 1.5,

                            sm: 2,

                            md: 2.5

                        }

                    }}
                >

                    <Typography
                        variant="h6"
                        sx={{

                            fontWeight: 700,

                            mb: 2

                        }}
                    >
                        Grand Total
                    </Typography>


                    <Grid
                        container
                        spacing={{

                            xs: 1.5,

                            sm: 2

                        }}
                    >

                        <Grid
                            item
                            xs={6}
                            sm={6}
                            md={2.4}
                        >

                            <TextField
                                fullWidth

                                label="No of Trolley"

                                value={
                                    grandTotal.nooftrolly
                                }

                                InputProps={{
                                    readOnly: true
                                }}

                            />

                        </Grid>


                        <Grid
                            item
                            xs={6}
                            sm={6}
                            md={2.4}
                        >

                            <TextField
                                fullWidth

                                label="Coach Set"

                                value={
                                    grandTotal.coachset
                                }

                                InputProps={{
                                    readOnly: true
                                }}

                            />

                        </Grid>


                        <Grid
                            item
                            xs={6}
                            sm={6}
                            md={2.4}
                        >

                            <TextField
                                fullWidth

                                label="Labour Cost"

                                value={
                                    grandTotal.labourcost
                                }

                                InputProps={{
                                    readOnly: true
                                }}

                            />

                        </Grid>


                        <Grid
                            item
                            xs={6}
                            sm={6}
                            md={2.4}
                        >

                            <TextField
                                fullWidth

                                label="Material Cost"

                                value={
                                    grandTotal.materialcost
                                }

                                InputProps={{
                                    readOnly: true
                                }}

                            />

                        </Grid>


                        <Grid
                            item
                            xs={12}
                            sm={12}
                            md={2.4}
                        >

                            <TextField
                                fullWidth

                                label="Total"

                                value={
                                    grandTotal.total
                                }

                                InputProps={{
                                    readOnly: true
                                }}

                                sx={{

                                    "& .MuiInputBase-root": {

                                        fontWeight: 700

                                    }

                                }}

                            />

                        </Grid>

                    </Grid>

                </CardContent>

            </Card>


            {/* =====================================================
                DELETE DIALOG
            ===================================================== */}

            <Dialog

                open={
                    deleteDialog
                }

                onClose={() =>
                    setDeleteDialog(false)
                }

                fullWidth

                maxWidth="sm"

                PaperProps={{

                    sx: {

                        width: "100%",

                        m: {

                            xs: 1,

                            sm: 2

                        }

                    }

                }}

            >

                <DialogTitle>

                    Delete IOH Entry?

                </DialogTitle>


                <DialogContent>

                    Are you sure you want to
                    delete this entry?

                    <Box sx={{ mt: 2 }}>

                        <Typography>
                            <strong>
                                Railway:
                            </strong>{" "}
                            {
                                selectedRow?.rail
                            }
                        </Typography>

                        <Typography>
                            <strong>
                                Trolley:
                            </strong>{" "}
                            {
                                selectedRow?.trolly_type
                            }
                        </Typography>

                        <Typography>
                            <strong>
                                Sr No:
                            </strong>{" "}
                            {
                                selectedRow?.srno
                            }
                        </Typography>

                    </Box>

                </DialogContent>


                <DialogActions
                    sx={{

                        px: 2,

                        pb: 2,

                        gap: 1,

                        flexWrap: "wrap"

                    }}
                >

                    <Button

                        onClick={() =>
                            setDeleteDialog(
                                false
                            )
                        }
                    >
                        Cancel
                    </Button>


                    <Button

                        color="error"

                        variant="contained"

                        onClick={
                            handleDelete
                        }

                        disabled={
                            loading
                        }
                    >
                        Delete
                    </Button>

                </DialogActions>

            </Dialog>

        </Box>

    );

}


// ================================================================
// TABLE STYLES
// ================================================================

const tableHeaderStyle = {

    padding: "10px",

    borderBottom:
        "1px solid #ddd",

    textAlign: "center",

    whiteSpace: "nowrap",

    fontWeight: 700,

    backgroundColor: "#f5f5f5"

};


const tableCellStyle = {

    padding: "10px",

    borderBottom:
        "1px solid #eee",

    textAlign: "center",

    whiteSpace: "nowrap"

};